/**
 * computeDailyRevenue — pure helper extracted from coreSlice.nextDay
 * (03/06/2026 round 2 refactor of the daily P&L block).
 *
 * Returns the day revenue + the map of contracts whose delivery was
 * missed today (vehicle unavailable / driver sick / etc.). The caller
 * is responsible for applying the difficulty multiplier and the strike
 * modifier on top of the returned `dayRevenue`.
 *
 * Side-effects: none.
 *
 * @param {Object} state - the current game state snapshot
 * @param {boolean} working - true when isWorkingDay(state.day)
 * @param {Object} opts
 * @param {number} opts.supportRevenueBonus - cumulative multiplier from
 *   hired support roles (e.g. Marc, Sylvie). 0 if none.
 * @returns {{ dayRevenue: number, contractMisses: Object<string, true> }}
 */
import { INVESTMENTS } from '../../data/investments';
import { getDepotContractMultiplier, isForceInvestActive, FORCE_INVEST_PRODUCTIVITY_PENALTY } from '../../data/depotEconomics';
import { getDriverSpecialties } from '../../utils/driverSpecialties';
import { getManagerRevenueMultiplier } from '../../utils/managerBonuses';
import { effectiveGlobalBonus, computeMarginPressure } from '../../data/economyBalance';

export function computeDailyRevenue(state, working, opts = {}) {
  const supportRevenueBonus = opts.supportRevenueBonus || 0;
  const contractMisses = {};
  let dayRevenue = 0;

  // 13/07/2026 — Équilibrage : pression concurrentielle au-delà de 50 % de
  // marge nette (30 j glissants). Les contrats AO (`tenderWon`) gardent
  // leurs prix contractuels. cf. data/economyBalance.js.
  const marginPressure = computeMarginPressure(state);
  const pressured = (amount, contract) =>
    contract && contract.tenderWon ? amount : amount * marginPressure.factor;

  if (!working) {
    return { dayRevenue: 0, contractMisses, marginPressure };
  }

  state.activeContracts.forEach(contract => {
    if (contract.vehicleId && contract.driverId) {
      // Detect "missed delivery" — assigned vehicle in maintenance OR
      // assigned driver sick / on strike / on vacation / training.
      const v = state.vehicles.find(x => x.id === contract.vehicleId);
      const d = state.drivers.find(x => x.id === contract.driverId);
      // 12/02/2026 (fix user prod « missions réalisées sans chauffeurs »)
      // — Si le driverId du contrat pointe vers un chauffeur qui n'existe
      // plus (démission / M&A / bug), on ne paye AUCUN revenu. Avant, le
      // contrat continuait de générer du CA fantôme.
      if (!d) {
        contractMisses[contract.instanceId] = 'stale_driver';
        return;
      }
      const vehicleUnavailable = v && (v.status === 'maintenance' || v.status === 'broken');
      // 11/06/2026 — Holiday gating : a driver on holiday makes the
      // contract miss UNLESS the vehicle has an active interim
      // (`vehicle.useInterim === true`). In that case the contract
      // continues but a daily interim cost is added in coreSlice. The
      // contract revenue itself remains unchanged.
      // 12/02/2026 (fix user prod « sous-traitance activée dans message
      // ne s'est pas appliquée ») — On étend la reconnaissance de
      // l'intérim actif au flag `subcontracted:true` posé par le choix
      // "sous-traiter" du message de congé (messageSlice.driverVacation).
      // Sans ça, la trésorerie payait le coût de sous-traitance mais le
      // revenu du contrat était perdu (double perte).
      const driverOnHoliday = d && (d.onHoliday === true || d.status === 'holiday' || d.status === 'vacation');
      // 18/06/2026 (fix « arrêt maladie fantôme ») — un arrêt maladie couvert
      // par un intérimaire (evt-rh-02 choix 0, flag vehicle.subcontracted)
      // maintient aussi le contrat, comme les congés sous-traités.
      const driverAwayCovered = driverOnHoliday || (d && d.status === 'sick');
      const today = state.totalDays || 0;
      const subcontractActive = v && v.subcontracted === true
        && (v.subcontractUntil || 0) > today && driverAwayCovered;
      const interimActive = v && ((v.useInterim === true && driverOnHoliday) || subcontractActive);
      const driverUnavailable = d && !interimActive && (
        d.status === 'sick' || d.status === 'striking'
        || d.status === 'training' || d.status === 'vacation'
        || driverOnHoliday
      );
      if (vehicleUnavailable || driverUnavailable) {
        // 10/02/2026 (Lot N P1) — Retour user prod : « Sur 90j, c'est
        // normal qu'on puisse s'attendre à quelques absences. Pas sur du
        // ponctuel. » Tolérance accrue sur contrats longs :
        //   • Contrat court (duration < 7 jours ou frequency='ponctuel')
        //     → comportement actuel : missed = 0 revenu
        //   • Contrat long ET absence ≤ 5 j cumulés → revenu plein (toléré)
        //   • Contrat long ET absence > 5 j PLANIFIÉE (holidayPlanned) → -3 %
        //   • Contrat long ET absence > 5 j NON PLANIFIÉE → -10 %
        const isLongContract = (contract.duration || 0) >= 7
          && contract.frequency !== 'ponctuel';
        const missCount = (contract.missedDeliveries || 0) + 1;
        if (isLongContract && missCount <= 5) {
          // Tolérance : aucune pénalité, contrat continue normalement.
          dayRevenue += pressured(contract.dailyRevenue, contract);
          contractMisses[contract.instanceId] = 'tolerated';
          return;
        }
        if (isLongContract && missCount > 5) {
          // Détérioration selon planification.
          const planned = d && d.holidayPlanned === true;
          const penaltyRatio = planned ? 0.03 : 0.10;
          dayRevenue += pressured(contract.dailyRevenue * (1 - penaltyRatio), contract);
          contractMisses[contract.instanceId] = planned ? 'long_planned' : 'long_unplanned';
          return;
        }
        // Contrat court ou ponctuel : comportement strict.
        contractMisses[contract.instanceId] = true;
        return;
      }
      let revenue = contract.dailyRevenue;
      // Depot bonus : 2nd depot (regional) adds +8% on contracts < 200 km;
      // 3rd depot (national) adds +5% on > 500 km. Legacy contracts fall
      // back to 100 km (regional bonus may apply).
      const distanceKm = typeof contract.distanceKm === 'number' ? contract.distanceKm : 100;
      revenue *= getDepotContractMultiplier(state.ownedDepots || [], distanceKm);
      const driver = state.drivers.find(dd => dd.id === contract.driverId);
      if (driver) {
        const cat = contract.category;
        const specs = getDriverSpecialties(driver);
        const has = (s) => specs.includes(s);
        const specMatch = specs.includes(cat)
          || (cat === 'Messagerie / Colis' && has('Messagerie/Colis'))
          || (cat === 'BTP / Matériaux' && has('BTP/Matériaux/Engins TP'))
          || (cat === 'Spécialisé' && has('ADR/Produits sensibles'))
          || (cat === 'Export UE' && (has('Export UE') || has('Longue distance')));
        if (specMatch) {
          revenue *= 1.15;
        }
        revenue *= (1 + (driver.competence - 2) * 0.03);
      }
      // 17/06/2026 — Bonus des responsables de site (managerBonuses.js).
      // Multiplicateur appliqué selon type bonus + destination/tier.
      revenue *= getManagerRevenueMultiplier(contract, v, state.ownedDepots, state.hiredDepotManagers);
      // 08/02/2026 — Q2 Lot D : Boom sectoriel actif. Si le secteur du
      // contrat (catégorie ou sector du tender d'origine) matche le
      // marketBoom courant et qu'on est dans la fenêtre, +20 % de revenu.
      const boom = state.marketBoom;
      if (boom && boom.endDay > (state.totalDays || 0)) {
        const sector = (contract.sector || contract.category || '').toLowerCase();
        const theme = (boom.theme || '').toLowerCase();
        // Match permissif : contrat tagué `sector` directement OU catégorie
        // qui contient le thème (ex: 'distribution' matche 'Distribution').
        if (sector.includes(theme) || theme.includes(sector)) {
          revenue *= (1 + (boom.revenueBoost || 0.20));
        }
      }
      dayRevenue += pressured(revenue, contract);
    }
  });

  // 13/07/2026 — Équilibrage user : la somme linéaire (jusqu'à +255 %,
  // plafond +200 %) créait des marges impossibles. Le bonus global passe
  // dans une courbe de saturation à rendements décroissants plafonnée à
  // +50 % (cf. data/economyBalance.js) — chaque investissement/rôle garde
  // un effet visible, mais la courbe n'est plus exponentielle.
  const marketingBonus = Object.keys(state.activeInvestments).reduce((acc, invId) => {
    const inv = INVESTMENTS.find(i => i.id === invId);
    if (inv && inv.impactMultiplier > 0) return acc + inv.impactMultiplier;
    return acc;
  }, 0);
  dayRevenue *= (1 + effectiveGlobalBonus(marketingBonus + supportRevenueBonus));

  // Force-investment productivity penalty.
  // If the player has more than 8 vehicles but still operates a single
  // depot, saturation hurts everyone (−10 % until they invest in a 2nd
  // depot).
  if (isForceInvestActive(state)) {
    dayRevenue *= (1 - FORCE_INVEST_PRODUCTIVITY_PENALTY);
  }

  // 11/02/2026 (Phase 3 — scénario formateur pushé) — ZFE ban : pénalité
  // globale -20% sur les revenus si aucun véhicule n'est électrique
  // (proxy simple : véhicules non-EV limités en ville).
  if (state.zfeBan && (state.zfeBan.endDay || 0) >= (state.totalDays || 0)) {
    const evCount = (state.vehicles || []).filter((v) => v && v.energy === 'electric').length;
    if (evCount === 0) {
      dayRevenue *= 0.80;
    }
  }

  return { dayRevenue, contractMisses, marginPressure };
}
